import React, { useState } from 'react';
import { Script } from 'nordic/script';
import { Style } from 'nordic/style';
import { string, shape, bool, number, oneOf, arrayOf } from 'prop-types';
import feConfig from 'nordic/config';

import { Form as AndesForm } from '@andes/form';
import { Button } from '@andes/button';
import { ProgressIndicatorCircular } from '@andes/progress-indicator-circular';
import url from 'url';
import { Message } from '@andes/message';
import useModalFlow from '../../hooks/useModalFlow';
import ListLocations from '../../components/addresses-hub/ListLocations';
import AnotherLocation from '../../components/addresses-hub/AnotherLocation';
import Layout from '../../components/Layout';
import { track, checkIsCitiesEmpty } from '../../helpers';


import { checkOrigin } from '../../helpers/origin';
import {
  addCoachMarkDimensions,
  editAddressesURL as editAddressesURLHelper,
  getDefaultUrl,
  getLayoutType,
  getTranslations,
  isNativeApp,
  isShops,
  supportsExternalLinks,
} from '../../server/helpers/utils';
import {
  getUIComponents,
  getZipCodeTransform,
  stateCityComponentTransform,
} from '../../server/helpers/transformations';
import { useActionToPlatform } from '../../pages/addresses-hub/hooks';

const Locations = ({
  deviceType,
  device,
  analytics = null,
  melidata = null,
  title,
  addresses = null,
  zipCode = null,
  createAction = null,
  lowEnd,
  backUrl = '',
  isEmbed,
  subtitle,
  webView,
  translations,
  zipCodeValidations,
  isFlow,
  isModal,
  stateCity = null,
  editAddressesURL,
  selectedLocationLabel = null,
  selectLocationLabel,
  isLoggedIn,
  selectedCP = null,
  shouldRenderExternalLinks,
  appId,
  isNative,
  messages = null,
}) => {
  let stateCityAction;
  if (stateCity) {
    const isCitiesEmpty = checkIsCitiesEmpty(stateCity);
    stateCityAction = lowEnd && isCitiesEmpty ? 'state-city-cities' : 'state-city';
  }

  const initialZipCode = zipCode && zipCode.zipcode;
  const initialCity = stateCity && stateCity.cities && stateCity.cities.selected;
  const initialState = stateCity && stateCity.states && stateCity.states.selected;
  const initialSelectedAddress = initialZipCode ? null : addresses && addresses.selected;

  const [showSpinner, setShowSpinner] = useState(false);
  const [isScroll, setIsScroll] = useState(false);
  const [formAction, setFormAction] = useState('addresses');
  const [selectedAddress, setSelectedAddress] = useState(initialSelectedAddress);
  const [currentZipCode, setCurrentZipCode] = useState(initialZipCode);
  const [currentCity, setCurrentCity] = useState(initialCity);
  const [currentState, setCurrentState] = useState(initialState);

  const [errors, setErrorsState] = useState();

  const { setUpdateCurrentLocation } = useActionToPlatform('addressesForm', setErrorsState, setShowSpinner, {
    isEmbed,
    isFlow,
    isModal,
    webView,
    deviceType,
    propDevice: device,
  });

  useModalFlow(deviceType, isFlow);

  const handleClickSubmitButton = (event) => {
    let action = 'addresses';
    if (currentZipCode) {
      action = 'zipcode';
    }
    if (currentState && currentState !== '0' && currentCity && currentCity !== '0') {
      action = stateCityAction;
    }
    setFormAction(action);

    // Verify parent url and target origin
    const targetOrigin = checkOrigin(event, backUrl || createAction?.action);
    if (!targetOrigin) return;

    window.parent.postMessage({ type: 'nav:cp:redirect' }, targetOrigin);
    track('hub', 'select_address');

    if (!webView) {
      setShowSpinner(true);
    }
  };

  const sendPostMessages = () => {
    window.parent.postMessage({ type: 'nav:cp:close' }, backUrl);
  };

  const handleClickCancelButton = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const action = (isEmbed && !isFlow) || isModal ? sendPostMessages() : (window.location = backUrl);
    return action;
  };

  const handleAddressSelectionChange = (value) => {
    setSelectedAddress(value);
    if (zipCode) {
      setCurrentZipCode(null);
    }
    if (stateCity) {
      setCurrentCity('0');
      setCurrentState('0');
    }
  };

  const handleSubmitForm = (event) => {
    event.preventDefault();
    if (!webView) setShowSpinner(true);
    setUpdateCurrentLocation(event);
  };

  const shouldDisplayAlertMessage = Boolean(messages?.length);
  const shouldDisplayAnotherLocation = Boolean(zipCode) || Boolean(stateCity);

  return (
    <>
      <Layout
        title={title}
        subtitle={subtitle}
        deviceType={deviceType}
        backUrl={backUrl}
        lowEnd={lowEnd}
        isEmbed={isEmbed}
        webView={webView}
        isFlow={isFlow}
        fullScreen={!isModal}
        isScroll={isScroll}
        hasAddresses={!!addresses}
        closeButtonSrLabel={translations.close}
      >
        <AndesForm method="POST" id="addressesForm" className="primary-form" onSubmit={handleSubmitForm}>
          {webView && <input type="hidden" name="deeplink" />}
          <input type="hidden" name="action" value={formAction} />
          {shouldDisplayAlertMessage && (
          <div className="addresses-hub-messages">
            {messages.map((message) => (
              <Message
                key={message.message}
                hierarchy={message.hierarchy}
                color={message.color}
              >
                {message.message}
              </Message>
            ))}
          </div>
          )}
          {addresses && (
          <ListLocations
            {...addresses}
            isNative={isNative}
            appId={appId}
            deviceType={deviceType}
            lowEnd={lowEnd}
            isStateCity={!!stateCity}
            translations={translations}
            editAddressesURL={editAddressesURL}
            selectedAddress={selectedAddress}
            selectionChanged={(value) => handleAddressSelectionChange(value)}
            setIsScroll={setIsScroll}
            webView={webView}
            isFlow={isFlow}
            shouldRenderExternalLinks={shouldRenderExternalLinks}
          />
          )}
          {addresses && zipCode && deviceType === 'mobile' && (
          <input type="hidden" name="zipcode" value={currentZipCode} />
          )}
          {addresses && stateCity && deviceType === 'mobile' && (
          <>
            <input type="hidden" name="city_id" value={currentCity} />
            <input type="hidden" name="state_id" value={currentState} />
          </>
          )}
          {addresses && shouldDisplayAnotherLocation && <div className="separator" />}
          {shouldDisplayAnotherLocation && (
          <AnotherLocation
            appId={appId}
            zipCode={zipCode}
            stateCity={stateCity}
            zipCodeValidations={zipCodeValidations}
            errors={errors}
            deviceType={deviceType}
            lowEnd={lowEnd}
            isEmbed={isEmbed}
            autoFocus={!addresses}
            isFlow={isFlow}
            isModal={isModal}
            hasAddresses={!!addresses}
            createAction={createAction}
            selectedLocationLabel={selectedLocationLabel}
            selectLocationLabel={selectLocationLabel}
            isLoggedIn={isLoggedIn}
            translations={translations}
            selectedCP={selectedCP}
            selectedAddress={selectedAddress}
            webView={webView}
            shouldRenderExternalLinks={shouldRenderExternalLinks}
            zipCodeSubmitted={() => setFormAction('zipcode')}
            zipCodeChanged={(value) => setCurrentZipCode(value)}
            stateCitySubmitted={() => setFormAction(stateCityAction)}
            cityChanged={(value) => setCurrentCity(value)}
            stateChanged={(value) => setCurrentState(value)}
            setShowSpinner={setShowSpinner}
            analytics={analytics}
            melidata={melidata}
          />
          )}
          {isLoggedIn && addresses && (
          <>
            <div className="separator" />
            <div className="button-wrapper" style={{ position: 'static' }}>
              {deviceType !== 'mobile' && (
              <Button
                className="button-wrapper__cancel"
                hierarchy="quiet"
                type="buttom"
                size="medium"
                data-testid="cancel-button"
                onClick={handleClickCancelButton}
              >
                {translations.cancel}
              </Button>
              )}
              <Button
                className="button-wrapper__apply"
                type="submit"
                size="medium"
                data-testid="apply-button"
                onClick={handleClickSubmitButton}
              >
                {deviceType === 'mobile' ? translations.applyAction : translations.saveChanges}
              </Button>
            </div>
            <div className="filler" />
          </>
          )}
        </AndesForm>
        {showSpinner && (
        <ProgressIndicatorCircular
          className="spinner"
          modifier="fullscreen"
          size={deviceType === 'mobile' ? 'small' : 'large'}
        />
        )}
      </Layout>
      {stateCity && <Style href="addresses.state.city.css" inline />}
      {isModal && (webView || deviceType === 'mobile') && <Style href="addresses.modal.css" inline />}
      {isEmbed && <Style href="addresses.embed.css" inline />}
      {isEmbed && <Script src="modal-embed.js" />}
    </>
  );
};

Locations.propTypes = {
  deviceType: string.isRequired,
  appId: string,
  analytics: shape({
    page: string,
    section: string,
  }),
  melidata: shape({
    path: string,
  }),
  addresses: shape({}),
  zipCode: shape({}),
  stateCity: shape({}),
  createAction: shape({
    action: string,
    label: string,
  }),
  title: string.isRequired,
  backUrl: string,
  lowEnd: bool.isRequired,
  isEmbed: bool.isRequired,
  subtitle: string,
  webView: bool.isRequired,
  errors: shape({}),
  translations: shape({
    applyAction: string.isRequired,
    required: string.isRequired,
    onlyNumbersZipCode: string.isRequired,
    minLengthZipCode: string.isRequired,
    showMore: string.isRequired,
    showLess: string.isRequired,
    editAddresses: string.isRequired,
    close: string.isRequired,
  }).isRequired,
  zipCodeValidations: shape({
    maxLength: number.isRequired,
  }).isRequired,
  isFlow: bool.isRequired,
  isModal: bool.isRequired,
  isNative: bool.isRequired,
  editAddressesURL: string.isRequired,
  selectedLocationLabel: shape({
    title: string.isRequired,
    label: string.isRequired,
  }),
  selectLocationLabel: string.isRequired,
  isLoggedIn: bool.isRequired,
  selectedCP: string,
  shouldRenderExternalLinks: bool.isRequired,
  siteId: string,
  device: shape({}).isRequired,
  messages: arrayOf(
    shape({
      color: oneOf(['accent', 'green', 'orange', 'red']),
      hierarchy: oneOf(['quiet', 'loud']),
      message: string,
    }),
  ),
};


export function getServerSideProps(req, res) {
  const uiComponents = getUIComponents(res.locals.locations);
  const type = getLayoutType(req.device.mobile, req.query.mode);
  const translations = getTranslations(req.i18n, req.platform);

  const zipCodeValidations = feConfig.get('zipCodeValidation', req.platform.id, req.platform.siteId);
  const melidata = feConfig.get('tracking.melidata');

  const {
    appId,
    locations,
    selectedLocationLabel,
    selectLocationLabel,
    selectedCP,
  } = res.locals;

  if (uiComponents.zipCode) {
    uiComponents.zipCode.zipcode = getZipCodeTransform(
      req,
      translations,
      uiComponents.zipCode.zipcode,
      zipCodeValidations,
    );

    if (!req.user) {
      uiComponents.zipCode.title = translations.anotherLocation;
    }
  }
  if (uiComponents.stateCity) {
    uiComponents.stateCity = stateCityComponentTransform(
      uiComponents.stateCity,
      req.session.cities,
      req.body.state_id || req.session.state || req.query.state_id,
      req.body.city_id || req.session.city,
      translations,
    );

    if (req.query.state_id && !req.session.cities) {
      uiComponents.stateCity.cities.values = null;
    }

    if (!req.user) {
      uiComponents.stateCity.title = translations.anotherLocation;
    }
  }
  const defaultUrl = getDefaultUrl(req);

  const isFromMercadoShops = isShops(req);
  const { errors } = req.session;

  req.session.errors = null;
  req.session.zipcode = null;
  req.session.state = null;
  req.session.city = null;
  req.session.cities = null;

  if (isFromMercadoShops) {
    addCoachMarkDimensions(req, {}, melidata);
  }

  const {
    mode,
    flow,
    modal,
    go,
  } = req.query;

  const {
    type: deviceType,
    webView,
    lowEnd,
  } = req.device;

  const redirectUrl = (path) => {
    const redirectPath = req.device.type === 'mobile' && path ? path : 'addresses-hub';
    return url.format({
      pathname: `${feConfig.ragnar.basePath}${redirectPath}`,
      query: req.query,
      host: req.get('host'),
      protocol: req.protocol,
    });
  };

  return {
    props: {
      type,
      errors,
      translations,
      deviceType,
      backUrl: go || defaultUrl,
      createAction: uiComponents.createAction,
      title: locations && locations.title,
      subtitle: locations && locations.subtitle,
      addresses: uiComponents.addresses,
      stateCity: uiComponents.stateCity,
      lowEnd,
      zipCode: uiComponents.zipCode,
      isEmbed: mode === 'embed',
      appId,
      webView,
      zipCodeValidations,
      melidata,
      siteId: req.platform.siteId,
      isFlow: flow === 'true',
      isModal: modal === 'true',
      isNative: isNativeApp(req),
      editAddressesURL: editAddressesURLHelper(req),
      selectedLocationLabel,
      selectLocationLabel,
      isLoggedIn: !!req.user,
      selectedCP,
      shouldRenderExternalLinks: !!(!req.device.webView || supportsExternalLinks(req)),
      actionsUrls: { navigateAddress: redirectUrl() },
      locale: req.i18n.locale,
      device: req.device,
    },
    settings: {
      title: locations && locations.title,
      navigation: {
        type,
      },
      appId,
      melidata,
      errors,
      addresses: uiComponents.addresses,
      zipCode: uiComponents.zipCode,
    },
  };
}

export const setPageSettings = ({ settings }) => ({
  title: settings.title,
  navigation: settings.navigation,
  melidata: { ...settings.melidata,
    event_data: {
      app: settings.appId,
      addresses_quantity:
      settings.addresses && settings.addresses.addresses.length ? settings.addresses.addresses.length : 0,
      has_errors: !!(settings.errors && settings.errors.zipcode),
      zipcode: (settings.zipCode && settings.zipCode.zipcode) || '',
      ...settings.melidata.event_data,
    },
  },
});

export const hydrate = 'hydrateRoot';

export default Locations;
